import {
  findTier1MacroTier,
  findTier6OptionsTier,
  RiskTierRow,
} from '../constants/tierPresets';
import { Account, AccountPosition } from '../types';
import { TierBudgetSnapshot } from '../types/tier1Portfolio';
import {
  ALL_RISK_TIER_NUMBERS,
  getTierCatalogEntry,
  RiskTierNumber,
  TIER_CATALOG,
  TierCatalogEntry,
} from './tierCatalog';
import { accountDetailPath } from '../utils/appRoutes';

export interface TierPositionCounts {
  active: number;
  pending: number;
  recommendations: number;
}

export interface TierSpectrumRow {
  riskTier: RiskTierNumber;
  catalog: TierCatalogEntry;
  slot: RiskTierRow | null;
  enabled: boolean;
  budget: TierBudgetSnapshot | null;
  positionCounts: TierPositionCounts;
  deepLink: string;
}

const ACTIVE_STATUSES = new Set(['ACTIVE']);
const PENDING_STATUSES = new Set([
  'APPROVED',
  'ENTRY_PENDING',
  'PENDING_MARKET_OPEN',
]);
const RECOMMENDATION_STATUSES = new Set(['RECOMMENDED']);

export function resolveSlotForRiskTier(
  account: Account | undefined,
  riskTier: RiskTierNumber,
): RiskTierRow | null {
  const tiers = account?.riskTiers;
  if (!tiers?.length) return null;

  if (riskTier === 1) {
    return findTier1MacroTier(tiers) ?? tiers.find((t) => t.riskTier === 1) ?? null;
  }
  if (riskTier === 6) {
    return findTier6OptionsTier(tiers) ?? tiers.find((t) => t.riskTier === 6) ?? null;
  }
  return tiers.find((t) => t.riskTier === riskTier) ?? null;
}

export function resolveRiskTierForPosition(
  position: AccountPosition,
  account?: Account,
): RiskTierNumber | 'unassigned' {
  if (position.tierId && account?.riskTiers?.length) {
    const slot = account.riskTiers.find((t) => t.id === position.tierId);
    if (slot) return slot.riskTier as RiskTierNumber;
  }
  if (position.positionOrigin === 'macro_etf_tier1') return 1;
  if (
    position.riskTier >= 1 &&
    position.riskTier <= 6 &&
    Number.isFinite(position.riskTier)
  ) {
    return position.riskTier as RiskTierNumber;
  }
  return 'unassigned';
}

export function countPositionsForTierId(
  positions: AccountPosition[],
  tierId: string | undefined,
): TierPositionCounts {
  const counts: TierPositionCounts = {
    active: 0,
    pending: 0,
    recommendations: 0,
  };
  if (!tierId) return counts;

  for (const p of positions) {
    if (p.tierId !== tierId) continue;
    const status = p.status ?? '';
    if (ACTIVE_STATUSES.has(status)) counts.active += 1;
    else if (PENDING_STATUSES.has(status)) counts.pending += 1;
    else if (RECOMMENDATION_STATUSES.has(status)) counts.recommendations += 1;
  }
  return counts;
}

export function buildTierSpectrum(
  account: Account,
  budgets: TierBudgetSnapshot[],
  positions: AccountPosition[],
): TierSpectrumRow[] {
  const accountId = account.id;
  return ALL_RISK_TIER_NUMBERS.map((riskTier) => {
    const catalog = TIER_CATALOG[riskTier];
    const slot = resolveSlotForRiskTier(account, riskTier);
    const budget =
      slot && slot.enabled
        ? budgets.find((b) => b.tierId === slot.id) ?? null
        : null;
    const positionCounts = slot
      ? countPositionsForTierId(positions, slot.id)
      : { active: 0, pending: 0, recommendations: 0 };

    return {
      riskTier,
      catalog,
      slot: slot ?? null,
      enabled: !!slot?.enabled,
      budget,
      positionCounts,
      deepLink: `${accountDetailPath(accountId)}?tier=${riskTier}`,
    };
  });
}

export type TierPositionBuckets = Record<
  RiskTierNumber | 'unassigned',
  AccountPosition[]
>;

export function groupPositionsByTier(
  positions: AccountPosition[],
  account?: Account,
): TierPositionBuckets {
  const buckets: TierPositionBuckets = {
    1: [],
    2: [],
    3: [],
    4: [],
    5: [],
    6: [],
    unassigned: [],
  };

  for (const position of positions) {
    const key = resolveRiskTierForPosition(position, account);
    buckets[key].push(position);
  }
  return buckets;
}

export function positionMatchesTierFilter(
  position: AccountPosition,
  account: Account | undefined,
  filter: 'all' | RiskTierNumber,
): boolean {
  if (filter === 'all') return true;
  return resolveRiskTierForPosition(position, account) === filter;
}

export function getTierCatalogEntryForPosition(
  position: AccountPosition,
  account?: Account,
): TierCatalogEntry | null {
  const key = resolveRiskTierForPosition(position, account);
  if (key === 'unassigned') return null;
  return getTierCatalogEntry(key);
}
